import React, { useState, useEffect } from 'react';
import { Upload, FileText, CheckCircle, Clock, Trash2, Download, Eye, ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const UploadsPage = ({ user }) => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    category: 'homework',
    description: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  
  // Check if user is admin/mentor
  const isAdminOrMentor = user && (
    user.role === 'admin' || 
    user.role === 'mentor' || 
    user.is_admin || 
    user.is_mentor ||
    ['obedansu1994@gmail.com', 'mentorship@obedansuministries.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk'].includes(user.email?.toLowerCase())
  );

  useEffect(() => {
    if (user?.id) {
      fetchUploads();
    }
  }, [user]);

  const fetchUploads = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/uploads/user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUploads(data);
      }
    } catch (error) {
      console.error('Error fetching uploads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For now, we'll use a placeholder URL since we don't have file storage set up
      setUploadForm({ ...uploadForm, file: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!uploadForm.title || !uploadForm.file) return;

    setUploading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description || '');
      
      const response = await fetch(`${backendUrl}/api/uploads/file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          // Don't set Content-Type header - browser will set it automatically with boundary
        },
        body: formData
      });

      if (response.ok) {
        setShowUploadModal(false);
        setUploadForm({ title: '', category: 'homework', description: '', file: null });
        fetchUploads();
        alert('File uploaded successfully!');
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteUpload = async (uploadId) => {
    if (!window.confirm('Are you sure you want to delete this upload?')) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/uploads/${uploadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        fetchUploads();
        alert('Upload deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const token = localStorage.getItem('access_token');
      const downloadUrl = `${backendUrl}${fileUrl}`;
      
      // Make authenticated request to get the file as blob
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      alert('File downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      homework: 'bg-blue-100 text-blue-800',
      project: 'bg-purple-100 text-purple-800',
      assessment: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading uploads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin/Mentor Banner */}
        {isAdminOrMentor && (
          <div className="bg-gradient-to-r from-orange-500 to-purple-600 rounded-xl p-4 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Eye className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold">Review Mentee Uploads</h3>
                  <p className="text-sm text-white/80">View and review all submissions from mentees</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/uploads')}
                className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-100 transition-colors"
              >
                Go to Admin Uploads
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-light text-gray-900">My Uploads</h1>
            <p className="text-gray-600 mt-2">Submit homework, projects, and assessments</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-medium flex items-center"
          >
            <Upload className="w-5 h-5 mr-2" />
            Upload Document
          </button>
        </div>

        {/* Uploads Grid */}
        {uploads.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No uploads yet</h3>
            <p className="text-gray-600 mb-6">Start by uploading your first document</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Upload Now
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {uploads.map((upload) => (
              <div key={upload.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm">{upload.title}</h3>
                      <p className="text-xs text-gray-500">{upload.file_name}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(upload.category)}`}>
                      {upload.category}
                    </span>
                    {upload.is_reviewed ? (
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Reviewed
                      </span>
                    ) : (
                      <span className="flex items-center text-orange-600">
                        <Clock className="w-4 h-4 mr-1" />
                        Pending
                      </span>
                    )}
                  </div>

                  {upload.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{upload.description}</p>
                  )}

                  {/* Review Status & Feedback */}
                  {upload.is_reviewed && (
                    <div className={`rounded-lg p-3 ${
                      upload.review_status === 'approved' || upload.status === 'approved' 
                        ? 'bg-green-50 border border-green-200' 
                        : upload.review_status === 'rejected' || upload.status === 'rejected'
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-blue-50 border border-blue-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        {(upload.review_status === 'approved' || upload.status === 'approved') ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (upload.review_status === 'rejected' || upload.status === 'rejected') ? (
                          <X className="w-4 h-4 text-red-600" />
                        ) : (
                          <Eye className="w-4 h-4 text-blue-600" />
                        )}
                        <span className={`text-xs font-medium ${
                          upload.review_status === 'approved' || upload.status === 'approved'
                            ? 'text-green-800' 
                            : upload.review_status === 'rejected' || upload.status === 'rejected'
                            ? 'text-red-800'
                            : 'text-blue-800'
                        }`}>
                          {(upload.review_status || upload.status || 'Reviewed').toUpperCase()}
                        </span>
                      </div>
                      {(upload.review_feedback || upload.review_notes) && (
                        <p className={`text-sm ${
                          upload.review_status === 'approved' || upload.status === 'approved'
                            ? 'text-green-700' 
                            : upload.review_status === 'rejected' || upload.status === 'rejected'
                            ? 'text-red-700'
                            : 'text-blue-700'
                        }`}>
                          <strong>Mentor Feedback:</strong> {upload.review_feedback || upload.review_notes}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    Uploaded {new Date(upload.uploaded_at).toLocaleDateString()}
                  </div>

                  <div className="flex items-center space-x-2 pt-3 border-t border-gray-100">
                    <button 
                      onClick={() => downloadFile(upload.file_url, upload.file_name)}
                      className="flex-1 bg-black text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 flex items-center justify-center"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </button>
                    <button
                      onClick={() => deleteUpload(upload.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Upload Document</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Title *
                </label>
                <input
                  type="text"
                  required
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Week 1 Homework"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="homework">Homework</option>
                  <option value="project">Project</option>
                  <option value="assessment">Assessment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  rows="3"
                  placeholder="Optional notes about this upload"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File * (PDF, DOCX, PPTX, JPG, PNG)
                </label>
                <input
                  type="file"
                  required
                  onChange={handleFileChange}
                  accept=".pdf,.docx,.pptx,.jpg,.jpeg,.png"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {uploadForm.file && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-gray-300"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadForm.file}
                  className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadsPage;
